import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, Bot, TrendingDown, Minus, Key, ShieldAlert, Languages, BarChart2, Sparkles, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { SecurityAnalysis, SecurityVulnerability } from '../types';
import { AiVerificationState } from '../hooks/useAiVerification';

interface Props {
  security: SecurityAnalysis;
  repoName: string;
  aiVerification?: AiVerificationState;
}

// ── SVG Donut Gauge ────────────────────────────────────────────────────────
function DonutGauge({ score, label, sublabel, size = 110 }: {
  score: number; label: string; sublabel: string; size?: number;
}) {
  const r = 42;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  const filled = (score / 100) * circ;
  const gap = circ - filled;

  const color =
    score >= 80 ? '#9FE870' :
    score >= 60 ? '#FFD43B' :
    score >= 40 ? '#FFA94D' : '#FF6B6B';

  const textColor =
    score >= 80 ? 'text-lime' :
    score >= 60 ? 'text-sev-medium' :
    score >= 40 ? 'text-sev-high' : 'text-sev-critical';

  return (
    <div className="flex flex-col items-center" data-testid={`gauge-${label.replace(/\s+/g, '-')}`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#242A27" strokeWidth={10} />
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={color}
          strokeWidth={10}
          strokeDasharray={`${filled} ${gap}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
        />
        <text x={cx} y={cy - 4} textAnchor="middle" dominantBaseline="middle"
          fontSize="16" fontWeight="bold" fill={color}>
          {score}%
        </text>
        <text x={cx} y={cy + 13} textAnchor="middle" fontSize="7.5" fill="#8B948F">
          {sublabel}
        </text>
      </svg>
      <p className={`text-xs font-semibold text-center mt-1 ${textColor}`}>{label}</p>
    </div>
  );
}

// ── Severity Badge ─────────────────────────────────────────────────────────
const SEV_STYLES: Record<string, string> = {
  critical: 'bg-sev-critical/10 text-sev-critical border-sev-critical/40',
  high:     'bg-sev-high/10 text-sev-high border-sev-high/40',
  medium:   'bg-sev-medium/10 text-sev-medium border-sev-medium/40',
  low:      'bg-sev-low/10 text-sev-low border-sev-low/40',
};
const SEV_DOT: Record<string, string> = {
  critical: 'bg-sev-critical', high: 'bg-sev-high', medium: 'bg-sev-medium', low: 'bg-sev-low',
};

function SevBadge({ sev }: { sev: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${SEV_STYLES[sev] ?? 'bg-gray-100 text-gray-600 border-gray-200'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${SEV_DOT[sev] ?? 'bg-gray-400'}`} />
      {sev.charAt(0).toUpperCase() + sev.slice(1)}
    </span>
  );
}

// ── Category tag — differentiates PII from secrets ─────────────────────────
function CategoryTag({ vuln }: { vuln: SecurityVulnerability }) {
  const isPii = vuln.id?.startsWith('pii-');
  if (isPii) {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-sev-critical/10 text-sev-critical">
        PII
      </span>
    );
  }
  const styles: Record<string, string> = {
    vibeCode:      'bg-accent-amber/10 text-accent-amber',
    secrets:       'bg-accent-teal/15 text-accent-teal',
    dependency:    'bg-gray-100 text-gray-700',
    configuration: 'bg-gray-100 text-gray-700',
    iac:           'bg-sev-low/10 text-sev-low',
    code:          'bg-accent-teal/15 text-accent-teal',
    compliance:    'bg-lime/10 text-lime',
  };
  const labels: Record<string, string> = {
    vibeCode: 'AI Risk', iac: 'IaC',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles[vuln.category] ?? 'bg-gray-100 text-gray-600'}`}>
      {labels[vuln.category] ?? vuln.category}
    </span>
  );
}

// ── Verdict Badge — backend AI verification result ─────────────────────────
const VERDICT_STYLES: Record<string, string> = {
  confirmed:       'bg-sev-critical/10 text-sev-critical border-sev-critical/40',
  false_positive:  'bg-lime/10 text-lime border-lime/40',
  needs_review:    'bg-sev-medium/10 text-sev-medium border-sev-medium/40',
};
const VERDICT_LABELS: Record<string, string> = {
  confirmed: 'Confirmed',
  false_positive: 'False Positive',
  needs_review: 'Needs Review',
};

function VerdictBadge({ verdict }: { verdict?: SecurityVulnerability['verdict'] }) {
  if (!verdict) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${VERDICT_STYLES[verdict] ?? ''}`}>
      {VERDICT_LABELS[verdict] ?? verdict}
    </span>
  );
}

const AI_STAGE_LABELS: Record<string, string> = {
  submitting: 'Submitting…',
  fetching: 'Fetching repository…',
  scanning: 'Scanning code…',
  indexing: 'Indexing…',
  verifying: 'Verifying findings…',
};

// ── Trend Chip ─────────────────────────────────────────────────────────────
function TrendChip({ count }: { count: number }) {
  if (count === 0) return <span className="text-xs text-gray-400 flex items-center gap-0.5"><Minus className="w-3 h-3" />0</span>;
  return (
    <span className="text-xs text-sev-critical flex items-center gap-0.5 font-medium">
      <TrendingDown className="w-3 h-3 rotate-180" />{count}
    </span>
  );
}

// ── Derive language from file extension ────────────────────────────────────
function langFromPath(path: string): string | null {
  const ext = path.split('.').pop()?.toLowerCase();
  const map: Record<string, string> = {
    ts: 'TypeScript', tsx: 'TypeScript', js: 'JavaScript', jsx: 'JavaScript',
    py: 'Python', java: 'Java', go: 'Go', php: 'PHP', rb: 'Ruby',
    cs: 'C#', cpp: 'C++', c: 'C', rs: 'Rust', kt: 'Kotlin', swift: 'Swift',
    tf: 'Terraform', yml: 'YAML', yaml: 'YAML',
  };
  return ext ? (map[ext] ?? null) : null;
}

// ── Main Dashboard ─────────────────────────────────────────────────────────
const SecurityDashboard: React.FC<Props> = ({ security, repoName, aiVerification }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Written as `aiVerification && ...` (not `aiVerification?.status === ...`)
  // so TypeScript actually narrows `aiVerification` to defined for the
  // `.findings` access on the right of the `&&` — optional chaining alone
  // doesn't reliably narrow across a subsequent property access.
  const aiDone = Boolean(aiVerification && aiVerification.status === 'done' && aiVerification.findings != null);
  const vulns = aiDone ? aiVerification!.findings! : security.vulnerabilities;
  const aiInProgress = aiVerification
    ? ['submitting', 'fetching', 'scanning', 'indexing', 'verifying'].includes(aiVerification.status)
    : false;

  const counts = useMemo(() => ({
    critical: vulns.filter(v => v.severity === 'critical').length,
    high:     vulns.filter(v => v.severity === 'high').length,
    medium:   vulns.filter(v => v.severity === 'medium').length,
    low:      vulns.filter(v => v.severity === 'low').length,
  }), [vulns]);

  // PII findings
  const piiFindings = useMemo(() => vulns.filter(v => v.id?.startsWith('pii-')), [vulns]);
  const secretFindings = useMemo(() => vulns.filter(v => v.category === 'secrets' && !v.id?.startsWith('pii-')), [vulns]);

  // Category breakdown (deduplicated by title)
  const categoryBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    vulns.forEach(v => {
      const key = v.id?.startsWith('pii-') ? 'pii' : v.category;
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([cat, count]) => ({ cat, count }));
  }, [vulns]);

  const categoryMax = useMemo(() => Math.max(...categoryBreakdown.map(c => c.count), 1), [categoryBreakdown]);

  // Language breakdown from affected file extensions
  const langBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    vulns.forEach(v => {
      v.affectedFiles?.forEach(f => {
        const lang = langFromPath(f);
        if (lang) map[lang] = (map[lang] || 0) + 1;
      });
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [vulns]);

  // Deduplicated top issues sorted by severity
  const topIssues: SecurityVulnerability[] = useMemo(() => {
    const sevOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
    const seen = new Set<string>();
    return [...vulns]
      .sort((a, b) => (sevOrder[a.severity] ?? 5) - (sevOrder[b.severity] ?? 5))
      .filter(v => {
        const key = v.title;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 8);
  }, [vulns]);

  // AI Misconfigurations
  const aiMisconfigs = useMemo(() => vulns.filter(v => v.category === 'vibeCode'), [vulns]);
  const aiCounts = {
    critical: aiMisconfigs.filter(v => v.severity === 'critical').length,
    high:     aiMisconfigs.filter(v => v.severity === 'high').length,
    medium:   aiMisconfigs.filter(v => v.severity === 'medium').length,
    low:      aiMisconfigs.filter(v => v.severity === 'low').length,
  };

  const llmScore  = Math.max(0, 100 - (security.vibeCodeAnalysis?.llmRiskScore ?? 0));

  // Calculate OWASP score from actual OWASP-mapped findings (code + config categories)
  // Penalties: critical=15, high=8, medium=4, low=1 — same weights as overall score
  const owaspVulns = (vulns ?? []).filter(v =>
    v.category === 'code' || v.category === 'configuration'
  );
  const owaspPenalty = owaspVulns.reduce((acc, v) => {
    const p = v.severity === 'critical' ? 15 : v.severity === 'high' ? 8 : v.severity === 'medium' ? 4 : 1;
    return acc + p;
  }, 0);
  const owaspScore = Math.max(0, 100 - owaspPenalty);
  const totalIssues = counts.critical + counts.high + counts.medium + counts.low;

  // AI-verified score: same penalty formula, but only over backend-owned
  // categories and only once verification has actually run, suppressing
  // anything the verifier confirmed as a false positive.
  const aiVerifiedScore = useMemo(() => {
    if (!aiDone) return null;
    const relevant = vulns.filter(v =>
      (v.category === 'code' || v.category === 'iac' || v.category === 'configuration') &&
      v.verdict !== 'false_positive'
    );
    const penalty = relevant.reduce((acc, v) => {
      const p = v.severity === 'critical' ? 15 : v.severity === 'high' ? 8 : v.severity === 'medium' ? 4 : 1;
      return acc + p;
    }, 0);
    return Math.max(0, 100 - penalty);
  }, [aiDone, vulns]);

  // "Issues by Category" is one nominal series: every bar wears chart slot 1,
  // identity comes from the row label, and label text stays in text tokens.
  const catStyle: Record<string, { bar: string; label: string; text: string }> = {
    code:          { bar: 'bg-chart-1', label: 'Code (SAST)',    text: 'text-gray-700' },
    secrets:       { bar: 'bg-chart-1', label: 'Secrets',        text: 'text-gray-700' },
    pii:           { bar: 'bg-chart-1', label: 'PII',            text: 'text-gray-700' },
    vibeCode:      { bar: 'bg-chart-1', label: 'AI Risk',        text: 'text-gray-700' },
    dependency:    { bar: 'bg-chart-1', label: 'Dependencies',   text: 'text-gray-700' },
    configuration: { bar: 'bg-chart-1', label: 'Configuration',  text: 'text-gray-700' },
    iac:           { bar: 'bg-chart-1', label: 'IaC',            text: 'text-gray-700' },
    compliance:    { bar: 'bg-chart-1', label: 'Compliance',     text: 'text-gray-700' },
  };

  return (
    <div className="space-y-4">

      {/* ── PII Alert Banner ───────────────────────────────────────────────── */}
      {piiFindings.length > 0 && (
        <div className="flex items-start gap-3 bg-sev-critical/10 border border-sev-critical/40 rounded-xl px-5 py-4">
          <ShieldAlert className="w-5 h-5 text-sev-critical flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-sev-critical">
              PII Detected — {piiFindings.length} instance{piiFindings.length > 1 ? 's' : ''} of hardcoded personal data
            </p>
            <p className="text-xs text-sev-critical mt-0.5">
              {Array.from(new Set(piiFindings.map(v => v.title.replace('PII Detected: ', '').replace(' Hardcoded in Source', '')))).join(', ')}.
              {' '}This may constitute a reportable data breach if the repository is or was public (GDPR Art. 33, HIPAA §164.400).
            </p>
          </div>
        </div>
      )}

      {/* ── Header Row ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">AI Security Posture</h2>
          <p className="text-xs text-gray-500">{repoName}</p>
        </div>
        <div className="flex items-center gap-3">
          {aiVerification?.available && (
            <button
              onClick={aiVerification.start}
              disabled={aiInProgress}
              aria-label="Run Deep Analysis"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border bg-accent-teal/10 text-accent-teal border-accent-teal/40 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {aiInProgress
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Sparkles className="w-3.5 h-3.5" />}
              {aiInProgress ? (AI_STAGE_LABELS[aiVerification.status] ?? 'Working…') : 'Run Deep Analysis'}
            </button>
          )}
          <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${
            security.riskLevel === 'critical' ? 'bg-sev-critical/10 text-sev-critical border-sev-critical/40' :
            security.riskLevel === 'high'     ? 'bg-sev-high/10 text-sev-high border-sev-high/40' :
            security.riskLevel === 'medium'   ? 'bg-sev-medium/10 text-sev-medium border-sev-medium/40' :
                                                'bg-limedim text-lime border-lime/40'
          }`}>
            {security.riskLevel.toUpperCase()} RISK
          </span>
        </div>
      </div>

      {aiVerification?.status === 'error' && aiVerification.error && (
        <div className="flex items-center gap-2 bg-sev-high/10 border border-sev-high/40 rounded-xl px-4 py-2.5 text-xs text-sev-high">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {aiVerification.error}
        </div>
      )}

      {aiDone && aiVerification?.summary && (
        <div className="flex items-start gap-2 bg-accent-teal/10 border border-accent-teal/40 rounded-xl px-4 py-3 text-xs text-gray-700">
          <Sparkles className="w-4 h-4 text-accent-teal flex-shrink-0 mt-0.5" />
          <p>{aiVerification.summary}</p>
        </div>
      )}

      {aiDone && aiVerification?.verificationMessage && (
        <div className="flex items-start gap-2 bg-sev-medium/10 border border-sev-medium/40 rounded-xl px-4 py-3 text-xs text-sev-medium">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <p>{aiVerification.verificationMessage}</p>
        </div>
      )}

      {/* ── Headline Stats ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Critical',        value: counts.critical,       color: 'text-sev-critical',    bg: 'bg-sev-critical/10 border-sev-critical/30' },
          { label: 'High',            value: counts.high,           color: 'text-sev-high', bg: 'bg-sev-high/10 border-sev-high/30' },
          { label: 'Secrets Exposed', value: secretFindings.length, color: 'text-accent-teal', bg: 'bg-accent-teal/10 border-accent-teal/30', icon: <Key className="w-3.5 h-3.5 text-accent-teal" /> },
          { label: 'PII Found',       value: piiFindings.length,    color: 'text-sev-critical',   bg: 'bg-sev-critical/10 border-sev-critical/30',    icon: <ShieldAlert className="w-3.5 h-3.5 text-sev-critical" /> },
        ].map(stat => (
          <div key={stat.label} className={`rounded-xl border px-4 py-3 ${stat.bg}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500 font-medium">{stat.label}</span>
              {stat.icon}
            </div>
            <span className={`text-2xl font-bold ${stat.color}`}>{stat.value}</span>
          </div>
        ))}
      </div>

      {/* ── Score Gauges + Category Breakdown + AI Misconfigs ─────────────── */}
      <div className="grid lg:grid-cols-3 gap-4">

        {/* Gauge panel */}
        <div className="bg-charcoal-card border border-gray-200 rounded-xl p-5">
          <p className="text-sm font-semibold text-gray-700 mb-4">Security Posture</p>
          <div className="flex justify-around flex-wrap gap-y-3">
            <DonutGauge score={owaspScore}  label="OWASP Top 10"     sublabel="Score" />
            <DonutGauge score={llmScore}    label="OWASP LLM Top 10 Signals" sublabel="Score" />
            {aiVerifiedScore !== null && (
              <DonutGauge score={aiVerifiedScore} label="AI-Verified" sublabel="Score" />
            )}
          </div>
        </div>

        {/* Category breakdown */}
        <div className="bg-charcoal-card border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart2 className="w-4 h-4 text-gray-500" />
            <p className="text-sm font-semibold text-gray-700">Issues by Category</p>
          </div>
          {categoryBreakdown.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-24">
              <CheckCircle className="w-8 h-8 text-lime mb-1" />
              <p className="text-sm text-lime font-medium">No issues found</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {categoryBreakdown.map(({ cat, count }) => {
                const style = catStyle[cat] ?? { bar: 'bg-chart-1', label: cat, text: 'text-gray-700' };
                const pct = Math.round((count / categoryMax) * 100);
                return (
                  <div key={cat}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium ${style.text}`}>{style.label}</span>
                      <span className="text-xs font-bold text-gray-700">{count}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full ${style.bar}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* AI Misconfigurations panel */}
        <div className="bg-charcoal-card border border-gray-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Bot className="w-4 h-4 text-amber-600" />
            <p className="text-sm font-semibold text-gray-700">AI Misconfigurations</p>
          </div>
          {aiMisconfigs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-24 text-center">
              <CheckCircle className="w-8 h-8 text-lime mb-2" />
              <p className="text-sm text-lime font-medium">No AI misconfigurations</p>
              <p className="text-xs text-gray-500">Clean vibe-code hygiene</p>
            </div>
          ) : (
            <div className="space-y-2">
              {[
                { label: 'Critical Severity', count: aiCounts.critical, color: 'text-sev-critical',    check: 'text-red-500' },
                { label: 'High Severity',     count: aiCounts.high,     color: 'text-sev-high', check: 'text-orange-500' },
                { label: 'Medium Severity',   count: aiCounts.medium,   color: 'text-sev-medium', check: 'text-yellow-500' },
                { label: 'Low Severity',      count: aiCounts.low,      color: 'text-sev-low',   check: 'text-blue-400' },
              ].filter(r => r.count > 0).map(row => (
                <div key={row.label} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className={`w-4 h-4 ${row.check}`} />
                    <span className="text-xs text-gray-700">{row.label}</span>
                  </div>
                  <span className={`text-sm font-bold ${row.color}`}>{row.count}</span>
                </div>
              ))}
              {security.vibeCodeAnalysis?.aiLibrariesDetected && security.vibeCodeAnalysis.aiLibrariesDetected.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {security.vibeCodeAnalysis.aiLibrariesDetected.slice(0, 4).map(lib => (
                    <span key={lib} className="text-xs bg-accent-amber/10 border border-accent-amber/30 text-accent-amber px-1.5 py-0.5 rounded font-mono">{lib}</span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Severity breakdown (slim row) ─────────────────────────────────── */}
      <div className="bg-charcoal-card border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold text-gray-700">Issues by Severity</p>
          <span className="text-xs text-gray-400">{totalIssues} total</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {([
            { label: 'Critical', key: 'critical', color: 'text-sev-critical',    dot: 'bg-sev-critical',    bar: 'bg-sev-critical' },
            { label: 'High',     key: 'high',     color: 'text-sev-high', dot: 'bg-sev-high', bar: 'bg-sev-high' },
            { label: 'Medium',   key: 'medium',   color: 'text-sev-medium', dot: 'bg-yellow-400', bar: 'bg-yellow-400' },
            { label: 'Low',      key: 'low',      color: 'text-sev-low',   dot: 'bg-sev-low',   bar: 'bg-sev-low' },
          ] as const).map(row => {
            const n = counts[row.key];
            const pct = totalIssues > 0 ? Math.round((n / totalIssues) * 100) : 0;
            return (
              <div key={row.key}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${row.dot}`} />
                    <span className="text-xs text-gray-600">{row.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendChip count={n} />
                    <span className={`text-sm font-bold ${row.color}`}>{n}</span>
                  </div>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full ${row.bar}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Top Issues table ──────────────────────────────────────────────── */}
      <div className="bg-charcoal-card border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="text-sm font-semibold text-gray-900">Top Issues</p>
            <p className="text-xs text-gray-500">{topIssues.length} of {totalIssues} findings</p>
          </div>
          {aiVerification?.available && (
            aiDone
              ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-accent-teal/10 text-accent-teal border border-accent-teal/40">Verified by AI</span>
              : <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 border border-gray-200">Preliminary — pattern-based</span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rule / Finding</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Category</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">OWASP</th>
                <th className="px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Severity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {topIssues.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-center text-gray-400">
                    <CheckCircle className="w-8 h-8 text-lime mx-auto mb-2" />
                    No security issues found
                  </td>
                </tr>
              ) : topIssues.map((v, i) => {
                const rowKey = v.id ?? String(i);
                const isExpanded = expandedId === rowKey;
                const canExpand = Boolean(v.verdict && v.verdictReasoning);
                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      className={`hover:bg-gray-50 transition-colors ${canExpand ? 'cursor-pointer' : ''}`}
                      onClick={canExpand ? () => setExpandedId(isExpanded ? null : rowKey) : undefined}
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          {v.category === 'vibeCode'
                            ? <Bot className="w-3.5 h-3.5 text-accent-amber flex-shrink-0" />
                            : v.id?.startsWith('pii-')
                              ? <ShieldAlert className="w-3.5 h-3.5 text-sev-critical flex-shrink-0" />
                              : <AlertTriangle className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          }
                          <span className="text-gray-900 font-medium text-xs leading-snug">{v.title}</span>
                          {canExpand && (isExpanded
                            ? <ChevronUp className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            : <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />)}
                        </div>
                        {v.affectedFiles && v.affectedFiles.length > 0 && (
                          <p className="text-xs text-gray-400 font-mono ml-5 mt-0.5 truncate max-w-xs">
                            {v.affectedFiles[0]}{v.lineNumber ? `:${v.lineNumber}` : ''}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <CategoryTag vuln={v} />
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-xs text-gray-500 leading-none">{v.owaspCategory?.split(' - ')[0] ?? v.cve ?? '—'}</span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5 flex-wrap">
                          <SevBadge sev={v.severity} />
                          <VerdictBadge verdict={v.verdict} />
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan={4} className="px-5 py-3">
                          <p className="text-xs text-gray-700">{v.verdictReasoning}</p>
                          {v.verdictEvidence && v.verdictEvidence.length > 0 && (
                            <ul className="mt-1.5 space-y-0.5">
                              {v.verdictEvidence.map((ev, ei) => (
                                <li key={ei} className="text-xs text-gray-500 font-mono">{ev}</li>
                              ))}
                            </ul>
                          )}
                          {v.verdictConfidence != null && (
                            <p className="text-xs text-gray-400 mt-1.5">Confidence: {Math.round(v.verdictConfidence * 100)}%</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Language breakdown chips */}
        {langBreakdown.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
              <Languages className="w-3.5 h-3.5" />
              Affected languages:
            </div>
            {langBreakdown.map(([lang, count]) => (
              <span key={lang} className="inline-flex items-center gap-1 text-xs bg-charcoal-card border border-gray-200 text-gray-700 px-2.5 py-1 rounded-full font-medium shadow-sm">
                {lang}
                <span className="bg-gray-100 text-gray-500 rounded-full px-1.5 py-0.5 text-xs leading-none">{count}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── ASVS + Compliance ─────────────────────────────────────────────── */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-charcoal-card border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">OWASP ASVS Level</p>
          <div className={`rounded-lg p-3 ${
            security.avsvLevel === 3 ? 'bg-limedim border border-lime/30' :
            security.avsvLevel === 2 ? 'bg-blue-50 border border-blue-200' :
            security.avsvLevel === 1 ? 'bg-sev-medium/10 border border-sev-medium/30' :
                                       'bg-sev-critical/10 border border-sev-critical/30'
          }`}>
            <p className={`text-base font-bold ${
              security.avsvLevel === 3 ? 'text-lime' :
              security.avsvLevel === 2 ? 'text-blue-700' :
              security.avsvLevel === 1 ? 'text-sev-medium' : 'text-sev-critical'
            }`}>
              {security.avsvLevel ? `Level ${security.avsvLevel} Ready` : 'Needs Remediation'}
            </p>
            <p className={`text-xs mt-0.5 ${
              security.avsvLevel === 3 ? 'text-lime' :
              security.avsvLevel === 2 ? 'text-sev-low' :
              security.avsvLevel === 1 ? 'text-sev-medium' : 'text-sev-critical'
            }`}>
              {security.avsvLevel === 3 && 'Advanced — healthcare / fintech ready'}
              {security.avsvLevel === 2 && 'Standard — suitable for business applications'}
              {security.avsvLevel === 1 && 'Basic — minimum security baseline met'}
              {!security.avsvLevel && 'Resolve critical/high issues to achieve Level 1'}
            </p>
          </div>
        </div>

        <div className="bg-charcoal-card border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Compliance Quick View</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'SOC 2',   ok: security.compliance.soxCompliant,   score: security.compliance.complianceReports?.sox?.score },
              { label: 'GDPR',    ok: security.compliance.gdprCompliant,  score: security.compliance.complianceReports?.gdpr?.score },
              { label: 'HIPAA',   ok: security.compliance.hipaaCompliant, score: security.compliance.complianceReports?.hipaa?.score },
              { label: 'PCI DSS', ok: security.compliance.pciCompliant,   score: security.compliance.complianceReports?.pci?.score },
            ].map(s => (
              <div key={s.label} className={`flex items-center justify-between p-2 rounded-lg border ${s.ok ? 'bg-limedim border-lime/30' : 'bg-sev-critical/10 border-sev-critical/30'}`}>
                <span className="text-xs font-semibold text-gray-700">{s.label}</span>
                <div className="flex items-center gap-1.5">
                  <span className={`text-xs font-bold ${s.ok ? 'text-lime' : 'text-sev-critical'}`}>{s.score ?? 0}%</span>
                  {s.ok
                    ? <CheckCircle className="w-3.5 h-3.5 text-lime" />
                    : <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};

export default SecurityDashboard;
